import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Col,
  Descriptions,
  Drawer,
  Empty,
  Row,
  Skeleton,
  Space,
  Typography,
  message,
} from "antd";
import {
  Download,
  FileSpreadsheet,
  FileText,
  Link2,
  Image as ImageIcon,
} from "lucide-react";
import { useLocaleStore } from "stores/localeStore";
import { downloadSupplierProductsZip } from "../hooks/downloadSupplierProductsZip";
import {
  useSupplierProductDetailQuery,
  type SupplierSheetFile,
} from "../hooks/useSupplierProductDetailQuery";

const { Text, Title } = Typography;

const MAX_BYTES = 100 * 1024 * 1024;

const DOC_ICON = {
  pdf: {
    Icon: FileText,
    color: "#cf1322",
    bg: "#fff1f0",
    border: "#ffccc7",
    label: "PDF",
  },
  xlsx: {
    Icon: FileSpreadsheet,
    color: "#389e0d",
    bg: "#f6ffed",
    border: "#b7eb8f",
    label: "XLS",
  },
  other: {
    Icon: FileText,
    color: "#595959",
    bg: "#fafafa",
    border: "#d9d9d9",
    label: "FIL",
  },
} as const;

function formatBytes(bytes: number): string {
  if (bytes >= 1024 ** 2) {
    return `${(bytes / 1024 ** 2).toFixed(1).replace(".", ",")} MB`;
  }
  return `${Math.round(bytes / 1024)} kB`;
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div>
    <Text
      strong
      style={{
        fontSize: 12,
        letterSpacing: 0.4,
        textTransform: "uppercase",
        color: "rgba(0,0,0,0.45)",
        display: "block",
        marginBottom: 10,
      }}
    >
      {title}
    </Text>
    {children}
  </div>
);

export type SupplierProductSheetProps = {
  open: boolean;
  productId: number | null;
  onClose: () => void;
};

export const SupplierProductSheet: React.FC<SupplierProductSheetProps> = ({
  open,
  productId,
  onClose,
}) => {
  const { locale } = useLocaleStore();
  const { data: product, isLoading, isFetching } = useSupplierProductDetailQuery(
    open ? productId : null,
    locale,
  );
  const [picked, setPicked] = useState<Set<number>>(() => new Set());
  const [downloading, setDownloading] = useState(false);

  const loading = isLoading || isFetching;

  useEffect(() => {
    if (!open || !product) {
      return;
    }
    // Default: select all files when opening a product.
    setPicked(
      new Set([...product.images, ...product.docs].map((file) => file.id)),
    );
  }, [open, product?.id]);

  const files = useMemo(
    () => [...(product?.images ?? []), ...(product?.docs ?? [])],
    [product],
  );

  const summary = useMemo(() => {
    let bytes = 0;
    files.forEach((file) => {
      if (picked.has(file.id)) {
        bytes += file.bytes;
      }
    });
    return { count: picked.size, bytes };
  }, [picked, files]);

  const overLimit = summary.bytes > MAX_BYTES;
  const allPicked = files.length > 0 && picked.size === files.length;

  const toggle = (id: number) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleAll = () => {
    setPicked(allPicked ? new Set() : new Set(files.map((file) => file.id)));
  };

  const copyLink = () => {
    message.info("Delningslänk är inte tillgänglig ännu");
  };

  const onDownload = async () => {
    if (!product || summary.count === 0 || overLimit || downloading) {
      return;
    }
    setDownloading(true);
    try {
      await downloadSupplierProductsZip({
        productIds: [product.id],
        mediaAssetIds: [...picked],
      });
      message.success("ZIP-filen laddas ner");
    } catch (error) {
      const text =
        error instanceof Error ? error.message : "Kunde inte ladda ner ZIP";
      message.error(text);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={440}
      destroyOnClose
      title={
        <Space align="start" size={12}>
          {product?.thumb ? (
            <img
              src={product.thumb}
              alt=""
              width={48}
              height={48}
              style={{
                borderRadius: 6,
                objectFit: "contain",
                background: "#fff",
                border: "1px solid #f0f0f0",
              }}
            />
          ) : (
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 6,
                background: "#f0f2f5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ImageIcon size={18} strokeWidth={1.5} color="rgba(0,0,0,0.25)" />
            </div>
          )}
          <div>
            <Title level={5} style={{ margin: 0 }}>
              {product?.name ?? (loading ? "Laddar…" : "Produkt")}
            </Title>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {product?.sku ?? "—"}
              {product?.ean ? ` · EAN ${product.ean}` : ""}
            </Text>
          </div>
        </Space>
      }
      headerStyle={{ paddingBlock: 16 }}
      bodyStyle={{ padding: "16px 20px" }}
      footerStyle={{ padding: "12px 20px 16px" }}
      footer={
        <Space direction="vertical" size={10} style={{ width: "100%" }}>
          <Row justify="space-between" align="middle">
            <Text
              type={overLimit ? "warning" : "secondary"}
              style={{ fontSize: 13 }}
            >
              {summary.count} filer valda · ~{formatBytes(summary.bytes)}
              {overLimit && " · över gränsen 100 MB"}
            </Text>
            <a onClick={toggleAll}>
              {allPicked ? "Avmarkera alla" : "Välj alla"}
            </a>
          </Row>
          <Space.Compact block>
            <Button
              block
              icon={<Link2 size={15} strokeWidth={1.75} />}
              onClick={copyLink}
            >
              Kopiera delningslänk
            </Button>
            <Button
              block
              type="primary"
              disabled={summary.count === 0 || overLimit || downloading}
              loading={downloading}
              icon={<Download size={15} strokeWidth={1.75} />}
              onClick={onDownload}
            >
              Ladda ner val (ZIP)
            </Button>
          </Space.Compact>
        </Space>
      }
    >
      {loading || !product ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : (
        <Space direction="vertical" size={20} style={{ width: "100%" }}>
          <Section title={`Bilder · ${product.images.length} st`}>
            {product.images.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Inga bilder"
              />
            ) : (
              <Row gutter={[10, 10]}>
                {product.images.map((img) => (
                  <Col span={8} key={img.id}>
                    <ImageTile
                      file={img}
                      selected={picked.has(img.id)}
                      onToggle={() => toggle(img.id)}
                    />
                  </Col>
                ))}
              </Row>
            )}
          </Section>

          <Section title={`Dokument · ${product.docs.length} st`}>
            {product.docs.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Inga dokument"
              />
            ) : (
              <div
                style={{
                  border: "1px solid #f0f0f0",
                  borderRadius: 6,
                  overflow: "hidden",
                }}
              >
                {product.docs.map((doc, i) => {
                  const meta = DOC_ICON[doc.docType] ?? DOC_ICON.other;
                  const Icon = meta.Icon;
                  return (
                    <Row
                      key={doc.id}
                      align="middle"
                      wrap={false}
                      style={{
                        gap: 12,
                        padding: "10px 12px",
                        borderTop: i ? "1px solid #f5f5f5" : "none",
                      }}
                    >
                      <Checkbox
                        checked={picked.has(doc.id)}
                        onChange={() => toggle(doc.id)}
                      />
                      <div
                        style={{
                          width: 30,
                          height: 30,
                          flex: "0 0 30px",
                          borderRadius: 4,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: meta.bg,
                          border: `1px solid ${meta.border}`,
                          color: meta.color,
                        }}
                      >
                        <Icon size={15} strokeWidth={1.75} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text
                          ellipsis
                          style={{ fontSize: 13, display: "block" }}
                        >
                          {doc.name}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {meta.label} · ~{formatBytes(doc.bytes)}
                          {doc.relationType ? ` · ${doc.relationType}` : ""}
                        </Text>
                      </div>
                      {doc.url ? (
                        <Button
                          type="text"
                          size="small"
                          icon={<Download size={15} strokeWidth={1.75} />}
                          href={doc.url}
                          target="_blank"
                          rel="noreferrer"
                        />
                      ) : null}
                    </Row>
                  );
                })}
              </div>
            )}
          </Section>

          <Section title="Produktdata">
            {product.specs.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Ingen produktdata"
              />
            ) : (
              <Descriptions
                size="small"
                column={1}
                colon={false}
                labelStyle={{ color: "rgba(0,0,0,0.45)", fontSize: 13 }}
                contentStyle={{ fontSize: 13, justifyContent: "flex-end" }}
                items={product.specs.map((s, i) => ({
                  key: i,
                  label: s.label,
                  children: s.value,
                }))}
              />
            )}
          </Section>
        </Space>
      )}
    </Drawer>
  );
};

const ImageTile: React.FC<{
  file: SupplierSheetFile;
  selected: boolean;
  onToggle: () => void;
}> = ({ file, selected, onToggle }) => (
  <div
    onClick={onToggle}
    style={{
      border: `1px solid ${selected ? "#195178" : "#f0f0f0"}`,
      borderRadius: 6,
      overflow: "hidden",
      cursor: "pointer",
    }}
  >
    <div
      style={{
        height: 74,
        background: file.thumb
          ? `#fff url(${file.thumb}) center/contain no-repeat`
          : "#f0f2f5",
        position: "relative",
      }}
    >
      <Checkbox
        checked={selected}
        style={{ position: "absolute", top: 6, right: 6 }}
        onClick={(e) => e.stopPropagation()}
        onChange={onToggle}
      />
    </div>
    <Row
      justify="space-between"
      wrap={false}
      style={{ padding: "6px 8px", gap: 6 }}
    >
      <Text ellipsis type="secondary" style={{ fontSize: 11 }}>
        {file.name}
      </Text>
      <Text type="secondary" style={{ fontSize: 11, whiteSpace: "nowrap" }}>
        ~{formatBytes(file.bytes)}
      </Text>
    </Row>
  </div>
);
